/**
 * PipelineSettingsPanel — Pipeline v2 Phase 3 (May 2026).
 *
 * Manager-only Settings panel that exposes the configurable thresholds
 * + Re-Engagement window for the lead pipeline.
 *
 * Backed by:
 *   GET /api/admin/pipeline-settings
 *   PUT /api/admin/pipeline-settings
 *   POST /api/admin/pipeline-settings/run-reengagement (manual trigger)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageSquare, Save, RotateCcw, PlayCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNEL_META = {
  call:  { icon: Phone,         label: "Call",  color: "#1a5c8a" },
  email: { icon: Mail,          label: "Email", color: "#5448a8" },
  sms:   { icon: MessageSquare, label: "SMS",   color: "#1a7a52" },
};
const ALL_LOSS_REASONS = [
  "Chose Competitor", "Price / Budget", "No Decision Made",
  "Not a Fit", "Bad Timing", "Unresponsive", "Invalid Lead",
];

export default function PipelineSettingsPanel({ token }) {
  const [settings, setSettings] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/pipeline-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setSettings(r.data); setOriginal(r.data); })
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load pipeline settings"));
  }, [token]);

  if (!settings) {
    return <div style={{ fontSize: 12, color: "var(--text-tertiary, #888)" }} data-testid="pipeline-settings-loading">Loading…</div>;
  }

  const dirty = JSON.stringify(settings) !== JSON.stringify(original);

  function setThreshold(ch, val) {
    const n = Math.max(1, Math.min(50, parseInt(val) || 1));
    setSettings(s => ({ ...s, attempt_thresholds: { ...s.attempt_thresholds, [ch]: n } }));
  }
  function setDays(val) {
    const n = Math.max(1, Math.min(365, parseInt(val) || 1));
    setSettings(s => ({ ...s, re_engagement_days: n }));
  }
  function toggleSubStatus(sub) {
    setSettings(s => {
      const cur = new Set(s.re_engagement_sub_statuses || []);
      if (cur.has(sub)) cur.delete(sub); else cur.add(sub);
      return { ...s, re_engagement_sub_statuses: Array.from(cur) };
    });
  }

  async function save() {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/pipeline-settings`, {
        attempt_thresholds: settings.attempt_thresholds,
        re_engagement_days: settings.re_engagement_days,
        re_engagement_sub_statuses: settings.re_engagement_sub_statuses,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
      setOriginal(res.data);
      toast.success("Pipeline settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  function revert() {
    setSettings(original);
  }

  async function runNow() {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/pipeline-settings/run-reengagement`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Re-Engagement sweep: ${res.data.created} task(s) created across ${res.data.scanned} candidate(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div data-testid="settings-pipeline">
      <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Pipeline Thresholds</h3>
      <p style={{ fontSize: 12, color: "var(--text-tertiary, #888)", marginBottom: 24 }}>
        Configure how many outreach attempts on one channel trigger the
        "Needs Attention" suggestion, and how long after Closed Lost a
        re-engagement task is auto-queued.
      </p>

      {/* Attempt thresholds */}
      <section style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Attempt thresholds (per channel)</div>
        <p style={{ fontSize: 11, color: "var(--text-tertiary, #888)", marginBottom: 12 }}>
          When a rep logs N or more attempts on the same channel in the current stage without advancing the lead,
          we surface it on the dashboard and flash an amber chip on Lead Focus.
        </p>
        <div style={{ display: "flex", gap: 12 }}>
          {["call", "email", "sms"].map(ch => {
            const Icon = CHANNEL_META[ch].icon;
            return (
              <div key={ch} style={{ flex: 1, padding: 12, borderRadius: 8, border: "0.5px solid var(--border)", background: "#fff" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, color: CHANNEL_META[ch].color, fontWeight: 600, fontSize: 12 }}>
                  <Icon style={{ width: 14, height: 14 }} /> {CHANNEL_META[ch].label}
                </div>
                <input
                  type="number"
                  min={1}
                  max={50}
                  data-testid={`pipeline-threshold-${ch}`}
                  value={settings.attempt_thresholds?.[ch] ?? 3}
                  onChange={e => setThreshold(ch, e.target.value)}
                  style={{ width: "100%", padding: "6px 8px", border: "0.5px solid var(--border)", borderRadius: 6, fontSize: 14, fontWeight: 600, textAlign: "center" }}
                />
                <div style={{ fontSize: 10, color: "var(--text-tertiary, #888)", marginTop: 6, textAlign: "center" }}>attempts</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Re-engagement */}
      <section style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Re-Engagement queue</div>
        <p style={{ fontSize: 11, color: "var(--text-tertiary, #888)", marginBottom: 12 }}>
          After this many days, Closed Lost leads with one of the selected reasons get an auto-created follow-up task.
          The loop runs hourly in the background.
        </p>

        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 11, color: "var(--text-tertiary, #888)", display: "block", marginBottom: 4 }}>Wait window</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="number"
                min={1}
                max={365}
                data-testid="pipeline-reengagement-days"
                value={settings.re_engagement_days || 90}
                onChange={e => setDays(e.target.value)}
                style={{ width: 100, padding: "6px 8px", border: "0.5px solid var(--border)", borderRadius: 6, fontSize: 14, fontWeight: 600, textAlign: "center" }}
              />
              <span style={{ fontSize: 12, color: "var(--text-tertiary, #888)" }}>days</span>
            </div>
          </div>
          <button
            onClick={runNow}
            disabled={running}
            data-testid="pipeline-run-reengagement-now"
            className="btn"
            style={{ padding: "8px 14px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: running ? "not-allowed" : "pointer", opacity: running ? 0.6 : 1 }}
          >
            <PlayCircle style={{ width: 12, height: 12 }} />
            {running ? "Running…" : "Run sweep now"}
          </button>
        </div>

        <div>
          <label style={{ fontSize: 11, color: "var(--text-tertiary, #888)", display: "block", marginBottom: 6 }}>Triggering loss reasons</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {ALL_LOSS_REASONS.map(r => {
              const active = (settings.re_engagement_sub_statuses || []).includes(r);
              const key = r.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleSubStatus(r)}
                  data-testid={`pipeline-reengagement-sub-${key}`}
                  style={{
                    padding: "5px 10px", borderRadius: 16, fontSize: 11,
                    border: `1px solid ${active ? "#5448a8" : "var(--border)"}`,
                    background: active ? "#ede9fe" : "#fff",
                    color: active ? "#4c1d95" : "var(--text-secondary, #555)",
                    fontWeight: active ? 600 : 400,
                    cursor: "pointer", fontFamily: "var(--font, inherit)",
                  }}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Action row */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "0.5px solid var(--border)", paddingTop: 16 }}>
        <button
          onClick={revert}
          disabled={!dirty}
          data-testid="pipeline-settings-revert"
          className="btn"
          style={{ padding: "8px 14px", fontSize: 12, opacity: dirty ? 1 : 0.5, cursor: dirty ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6 }}
        >
          <RotateCcw style={{ width: 12, height: 12 }} /> Revert
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="pipeline-settings-save"
          className="btn btn-primary"
          style={{ padding: "8px 14px", fontSize: 12, opacity: dirty && !saving ? 1 : 0.5, cursor: dirty && !saving ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6, background: "#5448a8", color: "#fff", border: "none" }}
        >
          <Save style={{ width: 12, height: 12 }} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {settings.updated_at && (
        <div style={{ fontSize: 10, color: "var(--text-tertiary, #888)", marginTop: 12, textAlign: "right" }}>
          Last updated {new Date(settings.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}
